import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { ProtectedLayout } from "./components/layout";
import { PersistentAudioElement } from "./components/audio";

// =====================
// Lazy-loaded Public Pages
// =====================
const LandingPage = lazy(() => import("./pages/Landing"));
const LoginPage = lazy(() => import("./pages/Login"));
const RegisterPage = lazy(() => import("./pages/Register"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPassword"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmail"));
const ResendVerificationPage = lazy(() => import("./pages/ResendVerification"));
const LocationDemoPage = lazy(() => import("./pages/LocationDemo"));

// Public pages (no auth required but invitation-aware)
const AcceptInvitationPage = lazy(() => import("./pages/AcceptInvitation"));

// =====================
// Lazy-loaded Protected Pages
// =====================
const DashboardPage = lazy(() => import("./pages/Dashboard"));

// Music
const ToursPage = lazy(() => import("./pages/Tours"));
const TourDetailPage = lazy(() => import("./pages/TourDetail"));

const EventsPage = lazy(() => import("./pages/Events"));
const EventDetailPage = lazy(() => import("./pages/EventDetail"));
const EventsCalendarPage = lazy(() => import("./pages/EventsCalendar"));

// Venues & Content
const VenuesPage = lazy(() => import("./pages/Venues"));
const VenueDetailPage = lazy(() => import("./pages/VenueDetail"));
const MusicPage = lazy(() => import("./pages/Music"));
const SongDetailPage = lazy(() => import("./pages/SongDetail"));
const PlaylistsPage = lazy(() => import("./pages/Playlists"));
const PlaylistDetailPage = lazy(() => import("./pages/PlaylistDetail"));
const SetlistsPage = lazy(() => import("./pages/Setlists"));
const SetlistDetailPage = lazy(() => import("./pages/SetlistDetail"));

// Financial
const FinancialOverviewPage = lazy(() => import("./pages/FinancialOverview"));
const TourFinancialDetailPage = lazy(() => import("./pages/TourFinancialDetail"));
const AccountingDashboardPage = lazy(() => import("./pages/AccountingDashboard"));

// Team
const EnsemblesPage = lazy(() => import("./pages/ensemble/Ensembles"));
const EnsembleDetailPage = lazy(() => import("./pages/ensemble/EnsembleDetail"));
const CollaboratorsPage = lazy(() => import("./pages/collaboration/Collaborators"));
const CollaboratorDetailPage = lazy(() => import("./pages/collaboration/CollaboratorDetail"));
const ContactsPage = lazy(() => import("./pages/Contacts"));
const ContactDetailPage = lazy(() => import("./pages/contact/ContactDetail"));

// Operations
const EquipmentPage = lazy(() => import("./pages/Equipment"));
const EquipmentDetailPage = lazy(() => import("./pages/EquipmentDetail"));
const RidersPage = lazy(() => import("./pages/Riders"));
const RiderDetailPage = lazy(() => import("./pages/RiderDetail"));
const MerchandisePage = lazy(() => import("./pages/Merchandise"));

// Analytics & Settings
const AnalyticsPage = lazy(() => import("./pages/Analytics"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const ProfilePage = lazy(() => import("./pages/Profile"));
const ArtistsPage = lazy(() => import("./pages/Artists"));

// New Migrated Pages
const AccommodationsPage = lazy(() => import("./pages/Accommodations"));
const AccommodationDetailPage = lazy(() => import("./pages/AccommodationDetail"));
const AdminPage = lazy(() => import("./pages/Admin"));
const EventsTimelinePage = lazy(() => import("./pages/EventsTimeline"));
const PlaylistEditPage = lazy(() => import("./pages/PlaylistEdit"));
const RiderFormPage = lazy(() => import("./pages/RiderForm"));
const SettingsProfilePage = lazy(() => import("./pages/SettingsProfile"));
const TodosPage = lazy(() => import("./pages/Todos"));

// AI
const AITourPlanningPage = lazy(() => import("./pages/AITourPlanning"));

// Error
const NotFoundPage = lazy(() => import("./pages/NotFound"));

// =====================
// Components
// =====================

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-full border-4 border-brand-500 border-t-transparent animate-spin" />
        <p className="text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

/**
 * ProtectedRoute - Layout Route
 * Uses Outlet so it stays mounted across child route changes.
 * Only renders once and persists across all protected pages.
 * Requires authentication - redirects to /login if not authenticated.
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
}

/**
 * PublicRoute - Wraps public pages with auth redirect
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// =====================
// App
// =====================

export default function App() {
  return (
    <>
      {/* Hidden <audio> element that persists across ALL page navigations.
          Must be OUTSIDE Suspense so it doesn't unmount during lazy-loading. */}
      <PersistentAudioElement />
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* ─── Public Routes (no sidebar) ─── */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/reset-password/:token" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
        <Route path="/verify-email" element={<PublicRoute><VerifyEmailPage /></PublicRoute>} />
        <Route path="/resend-verification" element={<PublicRoute><ResendVerificationPage /></PublicRoute>} />
        <Route path="/location-demo" element={<LocationDemoPage />} />
        <Route path="/invitation/:token" element={<AcceptInvitationPage />} />

        {/* ─── Protected Routes (with persistent sidebar) ─── */}
        {/* All routes here require authentication */}
        <Route element={<ProtectedRoute />}>
          <Route element={<ProtectedLayout />}>
            {/* Dashboard */}
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Tours */}
            <Route path="/tours" element={<ToursPage />} />
            <Route path="/events/tours" element={<ToursPage />} />
            <Route path="/tours/new" element={<TourDetailPage />} />
            <Route path="/tours/:id" element={<TourDetailPage />} />

            {/* Events */}
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/list" element={<EventsPage />} />
            <Route path="/events/calendar" element={<EventsCalendarPage />} />
            <Route path="/calendar" element={<EventsCalendarPage />} />
            <Route path="/events/:id" element={<EventDetailPage />} />

            {/* Financial */}
            <Route path="/financial" element={<FinancialOverviewPage />} />
            <Route path="/tours/:id/financial" element={<TourFinancialDetailPage />} />
            <Route path="/accounting" element={<AccountingDashboardPage />} />

            {/* Ensembles */}
            <Route path="/ensembles" element={<EnsemblesPage />} />
            <Route path="/ensembles/:id" element={<EnsembleDetailPage />} />

            {/* Contacts (replaces Collaborators) */}
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/contacts/new" element={<ContactDetailPage />} />
            <Route path="/contacts/:id" element={<ContactDetailPage />} />
            <Route path="/collaborators" element={<CollaboratorsPage />} />
            <Route path="/collaborators/:id" element={<CollaboratorDetailPage />} />

            {/* Equipment */}
            <Route path="/equipment" element={<EquipmentPage />} />
            <Route path="/equipment/:id" element={<EquipmentDetailPage />} />

            {/* Riders */}
            <Route path="/riders" element={<RidersPage />} />
            <Route path="/riders/:id" element={<RiderDetailPage />} />

            {/* Merchandise */}
            <Route path="/merchandise" element={<MerchandisePage />} />

            {/* Accommodations */}
            <Route path="/accommodations" element={<AccommodationsPage />} />
            <Route path="/accommodations/:id" element={<AccommodationDetailPage />} />

            {/* Todos */}
            <Route path="/todos" element={<TodosPage />} />

            {/* Admin (restricted to admin users) */}
            <Route path="/admin" element={<AdminPage />} />

            {/* Music Library */}
            <Route path="/music" element={<MusicPage />} />
            <Route path="/songs" element={<Navigate to="/music" replace />} />
            <Route path="/songs/:id" element={<SongDetailPage />} />

            {/* Venues & Content */}
            <Route path="/venues" element={<VenuesPage />} />
            <Route path="/venues/:id" element={<VenueDetailPage />} />
            <Route path="/playlists" element={<PlaylistsPage />} />
            <Route path="/playlists/:id" element={<PlaylistDetailPage />} />
            <Route path="/setlists" element={<SetlistsPage />} />
            <Route path="/setlists/:id" element={<SetlistDetailPage />} />

            {/* Artists */}
            <Route path="/artists" element={<ArtistsPage />} />

            {/* Riders (form page) */}
            <Route path="/riders/new" element={<RiderFormPage />} />
            <Route path="/riders/:id/edit" element={<RiderFormPage />} />

            {/* Playlist Edit */}
            <Route path="/playlists/new" element={<PlaylistEditPage />} />
            <Route path="/playlists/:id/edit" element={<PlaylistEditPage />} />

            {/* Events Timeline */}
            <Route path="/events/timeline/horizontal" element={<EventsTimelinePage />} />

            {/* AI */}
            <Route path="/ai/tour-planning" element={<AITourPlanningPage />} />

            {/* Analytics & Settings */}
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />
            <Route path="/settings/profile" element={<SettingsProfilePage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
        </Route>

        {/* ─── Catch-all ─── */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </>
  );
}
